import {
    RECIPE_PLACEHOLDER_KEYS,
    RECIPE_PLACEHOLDER_LABELS,
    RECIPE_PLACEHOLDER_SOURCES,
    type RecipePlaceholderKey,
} from '@/constants/recipePlaceholders';
import { Brand, Neutrals, accentAlpha } from '@/constants/theme';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

const THUMB_SIZE = 64;
const THUMB_GAP = 12;

/** Selected image: null = none, or a placeholder key */
export type SelectedRecipeImage = RecipePlaceholderKey | null;

interface RecipeImagePickerProps {
  value: SelectedRecipeImage;
  onChange: (value: SelectedRecipeImage) => void;
}

export function RecipeImagePicker({ value, onChange }: RecipeImagePickerProps) {
  return (
    <View style={styles.container}>
      <Text style={styles.label}>Recipe Image</Text>
      <Text style={styles.hint}>Choose a default image or leave empty</Text>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {/* None option */}
        <TouchableOpacity
          onPress={() => onChange(null)}
          style={[styles.thumb, value === null && styles.thumbSelected]}
          activeOpacity={0.8}
        >
          <View style={styles.noneThumb}>
            <Ionicons name="image-outline" size={28} color={Neutrals.textMuted} />
            <Text style={styles.noneLabel}>None</Text>
          </View>
        </TouchableOpacity>

        {RECIPE_PLACEHOLDER_KEYS.map((key) => (
          <TouchableOpacity
            key={key}
            onPress={() => onChange(key)}
            style={[styles.thumb, value === key && styles.thumbSelected]}
            activeOpacity={0.8}
          >
            <Image
              source={RECIPE_PLACEHOLDER_SOURCES[key]}
              style={styles.thumbImage}
              resizeMode="cover"
            />
            <Text style={styles.thumbLabel} numberOfLines={1}>
              {RECIPE_PLACEHOLDER_LABELS[key]}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 4,
  },
  hint: {
    fontSize: 12,
    color: Neutrals.textMuted,
    marginBottom: 12,
  },
  scrollContent: {
    flexDirection: 'row',
    gap: THUMB_GAP,
    paddingVertical: 4,
  },
  thumb: {
    width: THUMB_SIZE + 40,
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  thumbSelected: {
    borderColor: Brand.accent,
    backgroundColor: accentAlpha(0.1),
  },
  noneThumb: {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: 12,
    backgroundColor: Neutrals.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  noneLabel: {
    fontSize: 11,
    color: Neutrals.textSecondary,
    marginTop: 4,
  },
  thumbImage: {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: 12,
    backgroundColor: Neutrals.border,
  },
  thumbLabel: {
    fontSize: 11,
    color: Neutrals.textPrimary,
    marginTop: 4,
    textAlign: 'center',
    maxWidth: THUMB_SIZE + 20,
  },
});
